import throttle from 'lodash/throttle';
import { ReactNode, forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

import { mergeRefs } from '@tmh-website/services/utils.service';
import { Color } from '@tmh-website/shared/colors';

import * as Styled from './HorizontalScrollContainer.styles';

export interface HorizontalScrollContainerProps {
  fadeColor?: Color;
  iconColor?: Color;
  children: ReactNode;
  as?: 'nav' | 'div';
  ref?: any;
}

const HorizontalScrollContainer = forwardRef<HTMLDivElement, HorizontalScrollContainerProps>(
  ({ fadeColor = Color.Neutral_0_White, iconColor = Color.Blue_1000_Deep, as, children, ...props }, ref) => {
    const { t } = useTranslation();
    const [hasScrollIndicatorLeft, setHasScrollIndicatorLeft] = useState(false);
    const [hasScrollIndicatorRight, setHasScrollIndicatorRight] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    /**
     * Show hide scroll indicators for the LinksScrollContainer
     */
    useEffect(() => {
      const linkContainer = scrollContainerRef.current;
      if (!linkContainer) {
        return;
      }
      const ScrollThreshold = 16;

      const listener = () => {
        if (linkContainer.scrollWidth > linkContainer.offsetWidth) {
          setHasScrollIndicatorLeft(linkContainer.scrollLeft > ScrollThreshold);
          setHasScrollIndicatorRight(
            linkContainer.scrollWidth - linkContainer.offsetWidth - linkContainer.scrollLeft > ScrollThreshold
          );
        } else {
          setHasScrollIndicatorLeft(false);
          setHasScrollIndicatorRight(false);
        }
      };
      const throttledListener = throttle(listener, 250);

      window.addEventListener('resize', throttledListener);
      linkContainer.addEventListener('scroll', throttledListener);

      // Run on init
      setTimeout(listener, 100);

      return () => {
        window.removeEventListener('resize', throttledListener);
        linkContainer.removeEventListener('scroll', throttledListener);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollContainerRef.current]);

    /**
     * Scroll left/right within the LinksScrollContainer
     */
    function scrollPageLinks(direction: 'left' | 'right') {
      if (!scrollContainerRef.current) {
        return;
      }
      const delta = (direction === 'left' ? -1 : 1) * 320;
      const linkContainer = scrollContainerRef.current;
      linkContainer.scrollTo({ left: linkContainer.scrollLeft + delta, behavior: 'smooth' });
    }

    return (
      <>
        <Styled.ScrollIndicator
          $isActive={hasScrollIndicatorLeft}
          $position='left'
          $fadeColor={fadeColor}
          $iconColor={iconColor}
          onClick={() => scrollPageLinks('left')}
          aria-label={t('global:controls.scrollLeft') as string}
          type='button'>
          <CaretLeft weight='bold' />
        </Styled.ScrollIndicator>
        <Styled.ScrollContainer ref={mergeRefs(ref, scrollContainerRef)}>
          <Styled.EntriesContainer as={as ?? 'div'} {...props}>
            {children}
          </Styled.EntriesContainer>
        </Styled.ScrollContainer>
        <Styled.ScrollIndicator
          $isActive={hasScrollIndicatorRight}
          $position='right'
          $fadeColor={fadeColor}
          $iconColor={iconColor}
          onClick={() => scrollPageLinks('right')}
          aria-label={t('global:controls.scrollLeft') as string}
          type='button'>
          <CaretRight weight='bold' />
        </Styled.ScrollIndicator>
      </>
    );
  }
);

HorizontalScrollContainer.displayName = 'HorizontalScrollContainer';

export default HorizontalScrollContainer;
